import { AgentTaskStatus } from '../entities/AgentTask/AgentTask';

/**
 * The one authoritative computation of what a task's status MEANS, served on the wire so no client has
 * to derive it.
 *
 * ## Why this exists
 *
 * The rule lived in four hand-mirrored copies — web `lib/agent-task-buckets.ts`, the serverless
 * `agentTaskQueries.ts`, iOS `AgentTaskBucket`, Android `AgentTaskBucket` — each annotated "Shared 1:1
 * with…", each with its own green tests, and three of them wrong. A task read differently in the Island,
 * the chat cards and the Tasks screen because the three surfaces were answering from three rules, not
 * because their caches disagreed. `native-apps/shared/task-status/contract.json` pinned them together;
 * this makes the question one the clients do not have to answer at all.
 *
 * ## The contract with the clients
 *
 * `displayStatus` is ADDITIVE and the clients keep their local rule as a FALLBACK. A shipped phone that
 * has never heard of the field must render the task correctly, and a client newer than the server must
 * not require it. So: absent means "derive it yourself", never "no status".
 *
 * It carries an ENUM and flags, never an i18n key. The server must not own a translation — each surface
 * maps `bucket` onto its own key set (web `tasks.status.*`, native `agentTasks.tab.*`), which is also
 * what keeps a copy change a client change.
 *
 * ## Import-free, deliberately
 *
 * Only the status enum comes in. The rule is read by the serverless server, by orion-web's API routes,
 * and by this package's own tests; dragging the rpc barrel in here would make it unusable from a
 * CommonJS jest config (see orion-web's CLAUDE.md on the ESM-only `natural` dependency).
 */

/** The four buckets, spelled as the shared BI slug vocabulary spells them. */

/** The minimum a row needs to be given a display status. Satisfied by `AgentTask` plus its decorations. */

/** What the wire carries. Every field is DERIVED; none of them is a status the backend stores. */

/** A blocker that can never hold anything up again. FAILED still blocks: a dependent still needs it. */
function blockerSettled(status) {
  const s = (status ?? '').trim().toUpperCase();
  return s === 'COMPLETED' || s === 'CANCELLED' || s === 'MOOT';
}
function hasOpenDecision(task) {
  var _task$decisionPrompts;
  return (((_task$decisionPrompts = task.decisionPrompts) == null ? void 0 : _task$decisionPrompts.length) ?? 0) > 0;
}
function notBeforeMs(task) {
  if (!task.notBefore) {
    return null;
  }
  const ms = task.notBefore instanceof Date ? task.notBefore.getTime() : new Date(task.notBefore).getTime();
  // Never a throw and never a 0: `NaN` compares false against everything, so an unparseable date would
  // leave a row neither scheduled nor live.
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Which bucket a STATUS files under. ACTION_NEEDED and PAUSED → waiting; COMPLETED / CANCELLED / MOOT →
 * done (all terminal); FAILED → its own bucket (terminal, but must never read as a successful Done);
 * everything else — PENDING, IN_PROGRESS, WAITING, IN_REVIEW, BLOCKED and any status this build has
 * never seen — → inProgress.
 *
 * PENDING is deliberately inProgress: a queued task is the AGENT's turn, not the user's.
 *
 * MOOT is listed explicitly rather than left to the fall-through. A retired task — one the world moved
 * past before the agent got to it — would otherwise read as live work on every count and every tab.
 */
export function bucketOfStatus(status) {
  switch (status) {
    case AgentTaskStatus.ACTION_NEEDED:
    case AgentTaskStatus.PAUSED:
      return 'waiting';
    case AgentTaskStatus.COMPLETED:
    case AgentTaskStatus.CANCELLED:
    case AgentTaskStatus.MOOT:
      return 'done';
    case AgentTaskStatus.FAILED:
      return 'failed';
    default:
      return 'inProgress';
  }
}

/**
 * The whole derived verdict for one task.
 *
 * The precedence between the three quiet states is the part that took four implementations to agree on,
 * so it is written once here: **an open question outranks all of them** (the row is not "Later", not
 * "After {blocker}" and not "Not started" — it is waiting on the reader, and the quiet treatments would
 * mute the one row asking for something), then **a live run is by definition a start**, then scheduled
 * beats gated beats not-started.
 *
 * `blocked` promotes ONLY from `inProgress`. A terminal row stays terminal: the release pass is
 * asynchronous and a withdrawn question may never stamp its edge, so a stale prompt on a finished task
 * must not resurrect it into Action-needed with a live chip nothing the reader does could clear.
 */
/**
 * The row as it is the INSTANT the reader answers one of its questions, before any server round trip.
 *
 * Settling a decision patches `blockedByDecisions` under a field mask of exactly that one field and
 * never writes the task's status (`AgentTaskDecisionEdges.decisionSettled` in the VMR); the status only
 * moves later, when an agent picks the row up, which is async and unbounded. So a surface that waited
 * for a status change after the press waited for something that was not coming, and the row sat on
 * "Waiting on you" for as long as it took an agent to start. Reported as "it takes many many seconds
 * until the other indications are reflecting it".
 *
 * Dropping the answered question locally is enough to derive the true new state — `blocked` goes false,
 * the row leaves `waiting`, and the badge becomes Active Task — so the press is reflected on the spot
 * and the property is a pure function of the row rather than a race against a read.
 *
 * **It claims nothing else.** Not that the agent started (that is `inMotion`, which only a real verdict
 * sets), and not that the server accepted the answer. It is a COPY, so the caller still holds the
 * original to put back if the settle fails — optimism without a one-way door.
 *
 * Shared by web, iOS and Android so the three cannot disagree about what a press means.
 */
export function settleDecisionLocally(task, decisionId, choice) {
  const open = task.decisionPrompts ?? [];
  const left = open.filter(prompt => prompt.decisionId !== decisionId);
  if (left.length === open.length) {
    // Nothing on this row matched. Returning a copy anyway would make every caller re-render for a press
    // that changed nothing about it.
    return task;
  }
  // A hand-back question is the one exception to "settling never writes the status": its row sits in
  // ACTION_NEEDED by STATUS, not by the block, so dropping the prompt alone changes nothing on screen. Its
  // options say which status the settle writes, so the row can take it now.
  const target = settleTargetOf(task, decisionId, choice);
  return {
    ...task,
    decisionPrompts: left,
    ...(target ? {
      status: target
    } : {})
  };
}

/** The status the chosen option promises, when the answered question names one. */
function settleTargetOf(task, decisionId, choice) {
  var _prompt$options;
  if (!choice) {
    return undefined;
  }
  const prompt = (task.decisionPrompts ?? []).find(candidate => candidate.decisionId === decisionId);
  return prompt == null || (_prompt$options = prompt.options) == null || (_prompt$options = _prompt$options.find(option => option.id === choice)) == null ? void 0 : _prompt$options.settlesTaskTo;
}

/** How long a tapped answer's status outranks a server read that has not caught up with it yet. */
export const PENDING_SETTLE_HOLD_MS = 30_000;

/**
 * An answer whose status change the server has not shown yet.
 *
 * The settle that writes it is asynchronous (decision event → task event → transition, about 2.5 s), and a
 * surface re-reads the task as soon as the answer is recorded. That read, and a push for the decision's own
 * release, both arrive BEFORE the transition, still saying ACTION_NEEDED, and replace the row wholesale —
 * so the tap is undone on screen until some later frame. The hold keeps the answered status over any read
 * that still shows the old one, for a bounded time.
 */

/** The hold to keep for an answer, or undefined when the answer moves no status. */
export function pendingSettleFor(task, decisionId, choice, nowMs) {
  if (nowMs === void 0) {
    nowMs = Date.now();
  }
  const toStatus = settleTargetOf(task, decisionId, choice);
  if (!toStatus || !task.id || !task.status || task.status === toStatus) {
    return undefined;
  }
  return {
    taskId: task.id,
    decisionId,
    fromStatus: task.status,
    toStatus,
    untilMs: nowMs + PENDING_SETTLE_HOLD_MS
  };
}

/**
 * A server read seen through a pending answer: the read as-is once the server has moved off the status the
 * reader answered from (the settle landed, or something else happened to the task — either way the server
 * is now the newer word), once it carries an open question other than the one answered (a re-park), or once
 * the hold has run out; otherwise the read with the answer applied.
 *
 * `held` says whether the caller should keep the hold. A hold that is not kept is spent and dropped, so a
 * later, genuine return to the old status is never overridden.
 */
export function throughPendingSettle(incoming, pending, nowMs) {
  var _incoming$decisionPro;
  if (nowMs === void 0) {
    nowMs = Date.now();
  }
  if (incoming.id !== pending.taskId) {
    return {
      task: incoming,
      held: true
    };
  }
  const prompts = (_incoming$decisionPro = incoming.decisionPrompts) == null ? void 0 : _incoming$decisionPro.filter(prompt => prompt.decisionId !== pending.decisionId);
  // A question the reader did NOT answer is the server re-parking the task after the settle (a resumed run
  // that stopped again inside the hold): the read is newer than the tap even though its status matches the
  // one answered from, and holding it would hide the new question under "In progress" for the rest of the hold.
  if (nowMs > pending.untilMs || incoming.status !== pending.fromStatus || ((prompts == null ? void 0 : prompts.length) ?? 0) > 0) {
    return {
      task: incoming,
      held: false
    };
  }
  return {
    task: {
      ...incoming,
      status: pending.toStatus,
      ...(incoming.decisionPrompts ? {
        decisionPrompts: prompts
      } : {})
    },
    held: true
  };
}
export function taskDisplayStatus(task, nowMs) {
  if (nowMs === void 0) {
    nowMs = Date.now();
  }
  const blocked = hasOpenDecision(task);
  const byStatus = bucketOfStatus(task.status);
  const bucket = byStatus === 'inProgress' && blocked ? 'waiting' : byStatus;
  const pending = task.status === AgentTaskStatus.PENDING;
  const at = notBeforeMs(task);
  const scheduled = pending && !blocked && at !== null && at > nowMs;
  const gated = pending && !blocked && !scheduled && (task.blockedByTasks ?? []).some(b => !blockerSettled(b.status));
  const notStarted = pending && !blocked && task.runInFlight !== true && !scheduled && !gated && !(task.agentConversationId ?? '').trim();

  // The verdict, never a guess. `runInFlight` is the list's name and `isLive` the single-task read's, for
  // one fact; reading only one is how the island kept counting a task nobody had been on for seventeen
  // days. Absent BOTH means nothing computed it, and nothing computed it means no claim — there is
  // deliberately no bucket fallback here (see the contract's `status/in-progress` case).
  const inMotion = typeof task.runInFlight === 'boolean' ? task.runInFlight : task.isLive === true && !notStarted && !gated;
  return {
    bucket,
    inMotion,
    scheduled,
    gated,
    notStarted,
    blocked,
    showsLiveMarker: inMotion && bucket !== 'waiting'
  };
}

/** ASCII digits only — the same set Swift's `isASCII && isNumber` and Kotlin's `'0'..'9'` admit. */
const DIGITS = /^[0-9]+$/;

/**
 * Is `incoming` at least as new as `held` — may it replace the copy a client already has?
 *
 * `AgentTask.revision` is the SDL revision, monotonic per entity. It is the only thing on a task that
 * orders two reads, and until now it reached no client at all: neither web's `WebTask` nor the phones'
 * `SymphonyTask` carried it.
 *
 * Without an order, every cache is last-writer-wins, and the writers are not ordered. A list refresh, a
 * duplexer-driven re-read, a mutation echo and a per-card poll all land whenever they land — so a read
 * issued BEFORE a task finished can resolve after it and put "In progress" back over "Done". iOS's
 * at-work card already guards this locally with a monotonic read counter (`readsStarted` /
 * `newestReadApplied`); that is the same problem, solved once for one card.
 *
 * **Absent revisions compare as "accept".** A payload that does not carry one tells us nothing about
 * order, and refusing it would freeze a client whose server has not shipped the field — the opposite of
 * the intended failure. So this tightens ordering where the data allows it and changes nothing where it
 * does not.
 *
 * Compared NUMERICALLY. The revision is a digit string on the wire and crosses 10 → 9 lexicographically
 * backwards, so a text compare would silently reject newer rows from revision 10 on.
 *
 * **A pair this cannot order numerically ACCEPTS, exactly as an absent one does** — it does NOT fall back
 * to a text compare. For the fallback to run at all, one side must not be a number (a padded `'0009'`
 * still parses), and then a dictionary compare is a coin flip: `'rev-12'` vs `'8'` accepts, `'8'` vs
 * `'rev-12'` REFUSES. The refusal is the harmful half and it is the very failure the absent-revision case
 * above is written to avoid — a wrongly accepted row is corrected by the next read, while a wrongly
 * refused one leaves the cache believing it already holds the newest and nothing retries. So: cannot
 * order ⇒ do not block. Today every producer sends digits and this line is unreachable; it exists so that
 * the day one does not, statuses do not start sticking with no test red and nothing in the logs.
 */
export function revisionAtLeast(incoming, held) {
  if (!incoming || !held) {
    return true;
  }
  // DIGIT-ONLY, not `Number()`, and the strictness is the cross-surface contract rather than taste.
  // `Number` coerces what `Int`/`toLongOrNull` refuse — `Number(' ')` is 0, `' 12 '` is 12, `'1e3'` is
  // 1000, `'0x10'` is 16 — so web would take the numeric branch (and could REFUSE) on inputs the phones
  // send straight to the accept below. The three surfaces must route one input the same way.
  if (!DIGITS.test(incoming) || !DIGITS.test(held)) {
    return true;
  }
  return Number(incoming) >= Number(held);
}